// src/components/estimation/detailed/navigation/DetailedEstimationStepper.tsx
import React from 'react';
import { StepId } from '../types';

interface Step {
  number: number;
  id: StepId;
  label: string;
  sublabel?: string;
}

interface DetailedEstimationStepperProps {
  currentStep: StepId;
  completedSteps: string[];
  onStepClick: (stepId: StepId) => void;
}

const STEPS: Step[] = [
  { number: 1, id: 'description', label: "Description", sublabel: "& Études" },
  { number: 2, id: 'preparation', label: "Préparation" },
  { number: 3, id: 'demolition', label: "Démolition" },
  { number: 4, id: 'gros-oeuvre', label: "Gros œuvre" },
  { number: 5, id: 'vrd', label: "VRD" },
  { number: 6, id: 'isolation', label: "Isolation", sublabel: "& Étanchéité" },
  { number: 7, id: 'cloisons', label: "Cloisons", sublabel: "& Aménagement" },
  { number: 8, id: 'technique', label: "Électricité" },
  { number: 9, id: 'sanitaire', label: "Sanitaire", sublabel: "& Hygiène" },
  { number: 10, id: 'revetements', label: "Revêtements" },
  { number: 11, id: 'menuiserie', label: "Menuiserie" },
  { number: 12, id: 'finitions', label: "Finitions", sublabel: "& Cuisine" },
  { number: 13, id: 'exterieurs', label: "Extérieurs" },
  { number: 14, id: 'resultat', label: "Résultat" }
];

const DetailedEstimationStepper: React.FC<DetailedEstimationStepperProps> = ({
  currentStep,
  completedSteps,
  onStepClick
}) => {
  return (
    <div className="fixed top-16 left-0 right-0 bg-white shadow-md z-40">
      <div className="max-w-full px-4 py-4 overflow-x-auto">
        <div className="relative min-w-max">
          {/* Barre de progression */}
          <div className="absolute top-4 left-0 right-0 h-0.5 bg-gray-200">
            <div
              className="h-full bg-primary transition-all duration-300"
              style={{
                width: `${((STEPS.find(s => s.id === currentStep)?.number || 1) / STEPS.length) * 100}%`
              }}
            />
          </div>

          {/* Étapes */}
          <div className="flex justify-between relative">
            {STEPS.map((step) => {
              const isCompleted = completedSteps.includes(step.id);
              const isCurrent = currentStep === step.id;
              const isClickable = 
                (step.id === 'description' && isCompleted) ? false :
                step.number <= (STEPS.find(s => s.id === currentStep)?.number || 1) + 1;


              return (
                <div
                  key={step.id}
                  className={`
                    flex flex-col items-center min-w-[80px] px-2
                    ${isClickable ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}
                  `}
                  onClick={() => isClickable && onStepClick(step.id)}
                >
                  {/* Numéro de l'étape */}
                  <div
                    className={`
                      w-8 h-8 rounded-full flex items-center justify-center mb-2
                      transition-colors duration-200
                      ${isCurrent 
                        ? 'bg-primary text-white' 
                        : isCompleted
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-100 text-gray-400'
                      }
                      ${isClickable ? 'hover:ring-2 hover:ring-primary/50' : ''}
                    `}
                  >
                    {isCompleted ? '✓' : step.number}
                  </div>

                  {/* Label */}
                  <div className="text-center">
                    <div className={`
                      text-sm font-medium leading-tight
                      ${isCurrent ? 'text-primary' : 'text-gray-600'}
                    `}>
                      {step.label}
                    </div>
                    {step.sublabel && (
                      <div className="text-xs text-gray-400 leading-tight">
                        {step.sublabel}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailedEstimationStepper;
